import React from "react";
import { string, array, shape, object } from "prop-types";
import { ResponsiveImage, Link } from "@quintype/components";
import { Breadcrumb } from "../../atoms/Breadcrumb";
import { HeroTitle } from "../../atoms/HeroTitle";
import { Subtitle } from "../../atoms/Subtitle";
import { EventInfoLabel } from "../../atoms/EventInfoLabel";
import { EventInfoTitle } from "../../atoms/EventInfoTitle";
import "./hero-banner.m.css";

const imgMoleculeHeroBannerDesktop = "https://www.figma.com/api/mcp/asset/9d1b013b-430f-4e15-876f-881eff941f02";

// Session pages: skip compression and use max quality to preserve high-resolution CMS images
const SESSION_IMG_PARAMS = { auto: ["format"], q: 100 };
const DEFAULT_IMG_PARAMS = { auto: ["format", "compress"] };
// 2880px covers 2× DPR (retina) at a 1440px logical viewport width
const SESSION_WIDTHS = [768, 1024, 1440, 2880];

export const HeroBanner = ({ imageUrl, heroImageS3Key, heroImageMetadata, breadcrumbs, title, subtitle, eventInfo, storyTemplate }) => {
  return (
    <div styleName="hero-banner">
      {heroImageS3Key ? (
        <div styleName="hero-image">
          <ResponsiveImage
            slug={heroImageS3Key}
            metadata={heroImageMetadata}
            aspectRatio={[16, 9]}
            defaultWidth={1440}
            widths={storyTemplate === "session" ? SESSION_WIDTHS : [768, 1024, 1440]}
            imgParams={storyTemplate === "session" ? SESSION_IMG_PARAMS : DEFAULT_IMG_PARAMS}
            alt={title || "Hero Banner"}
          />
        </div>
      ) : (
        <img
          alt={title || ""}
          styleName="hero-image"
          src={imageUrl || imgMoleculeHeroBannerDesktop}
        />
      )}
      <div styleName="banner-content">
        <div styleName="hero-banner-unit-top">
          <div styleName="breadcrumb-container">
            {breadcrumbs &&
              breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index === 0 ? (
                    <Link href={`/events/${crumb.toLowerCase()}`} styleName="breadcrumb-link">
                      <Breadcrumb>{crumb}</Breadcrumb>
                    </Link>
                  ) : (
                    <Breadcrumb>{crumb}</Breadcrumb>
                  )}
                  {index < breadcrumbs.length - 1 && (
                    <span styleName="breadcrumb-separator">/</span>
                  )}
                </React.Fragment>
              ))}
          </div>
          {title && <div styleName="heading-wrapper">
            <HeroTitle>{title}</HeroTitle>
          </div>}
          {storyTemplate !== "session" && subtitle && <Subtitle style={{ color: 'var(--white)' }}>{subtitle}</Subtitle>}
        </div>
        {(eventInfo.when || eventInfo.time || eventInfo.arrival || eventInfo.where || eventInfo.sessionForum || eventInfo.departure) && (
          <div styleName={`event-info-unit ${storyTemplate === "session" ? "session-template" : ""}`}>
            {(eventInfo.when || eventInfo.time || eventInfo.arrival) && (
              <div styleName="event-info-group">
                {eventInfo.when && <div styleName="event-info-item">
                  <EventInfoLabel>When</EventInfoLabel>
                  <EventInfoTitle>{eventInfo.when}</EventInfoTitle>
                </div>}
                {eventInfo.time && (
                  <div styleName="event-info-item">
                    <EventInfoLabel>Time</EventInfoLabel>
                    <EventInfoTitle>{eventInfo.time}</EventInfoTitle>
                  </div>
                )}
                {eventInfo.arrival && (
                  <div styleName="event-info-item">
                    <EventInfoLabel>Arrival</EventInfoLabel>
                    <EventInfoTitle>{eventInfo.arrival}</EventInfoTitle>
                  </div>
                )}
              </div>
            )}
            {(eventInfo.where || eventInfo.sessionForum || eventInfo.departure) && (
              <div styleName="event-info-group">
                {eventInfo.where && <div styleName="event-info-item">
                  <EventInfoLabel>Where</EventInfoLabel>
                  <EventInfoTitle>{eventInfo.where}</EventInfoTitle>
                </div>}
                {eventInfo.sessionForum && <div styleName="event-info-item">
                  <EventInfoLabel>For whom</EventInfoLabel>
                  <EventInfoTitle>{eventInfo.sessionForum}</EventInfoTitle>
                </div>}
                {eventInfo.departure && (
                  <div styleName="event-info-item">
                    <EventInfoLabel>Departure</EventInfoLabel>
                    <EventInfoTitle>{eventInfo.departure}</EventInfoTitle>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

HeroBanner.propTypes = {
  imageUrl: string,
  heroImageS3Key: string,
  heroImageMetadata: object,
  breadcrumbs: array,
  title: string.isRequired,
  subtitle: string,
  storyTemplate: string,
  eventInfo: shape({
    when: string,
    arrival: string,
    where: string,
    departure: string,
  }),
};